import { ItemDisplayConfig } from "@services/tracpage/types/trac-page-items-config";

const updateMainPageOrderProp = (
  items: Record<string, ItemDisplayConfig>
): void => {
  const orderedItems = Object.entries(items)
    .sort(([, a], [, b]) => a.order - b.order)
    .filter(([, item]) => !item.isHidden)
    .slice(0, 3);

  orderedItems.forEach(([id], index) => {
    if (items[id].mainPageOrder === undefined) {
      items[id].mainPageOrder = index + 1;
    }
  });

  Object.entries(items)
    .sort(([, a], [, b]) => a.order - b.order)
    .slice(3)
    .forEach(([id]) => {
      delete items[id].mainPageOrder;
    });
};

export const updateOrder = (
  items: Record<string, ItemDisplayConfig>,
  itemId: string,
  newOrder: number
): Record<string, ItemDisplayConfig> => {
  const itemEntries = Object.entries(items).sort(
    ([, a], [, b]) => a.order - b.order
  );

  const currentIndex = itemEntries.findIndex(([id]) => id === itemId);
  const [itemToUpdate] = itemEntries.splice(currentIndex, 1);

  itemEntries.splice(newOrder - 1, 0, [
    itemToUpdate[0],
    { ...itemToUpdate[1] },
  ]);

  const updatedItems: Record<string, ItemDisplayConfig> = {};
  itemEntries.forEach(([id, item], index) => {
    updatedItems[id] = { ...item, order: index + 1 };
  });

  updateMainPageOrderProp(updatedItems);
  return updatedItems;
};

export const updateMainOrder = (
  items: Record<string, ItemDisplayConfig>,
  itemId: string,
  newMainOrder: number
): Record<string, ItemDisplayConfig> => {
  const updatedItems: Record<string, ItemDisplayConfig> = { ...items };

  const mainPageItems = Object.entries(updatedItems)
    .filter(([, item]) => item.mainPageOrder !== undefined)
    .sort(
      ([, a], [, b]) =>
        (a.mainPageOrder ?? Infinity) - (b.mainPageOrder ?? Infinity)
    );

  const currentIndex = mainPageItems.findIndex(([id]) => id === itemId);
  if (currentIndex !== -1) {
    mainPageItems.splice(currentIndex, 1);
  }

  mainPageItems.splice(newMainOrder - 1, 0, [itemId, updatedItems[itemId]]);

  mainPageItems.slice(0, 3).forEach(([id], index) => {
    const item = updatedItems[id];
    updatedItems[id] = { ...item, mainPageOrder: index + 1 };
  });

  mainPageItems.slice(3).forEach(([id]) => {
    const item = updatedItems[id];
    updatedItems[id] = { isHidden: item.isHidden, order: item.order };
  });

  return { ...updatedItems };
};

export const insertItem = (
  items: Record<string, ItemDisplayConfig>,
  newItemId: string,
  newItem: ItemDisplayConfig
): Record<string, ItemDisplayConfig> => {
  const itemEntries = Object.entries(items).sort(
    ([, a], [, b]) => a.order - b.order
  );

  itemEntries.splice(newItem.order - 1, 0, [newItemId, newItem]);

  const updatedItems: Record<string, ItemDisplayConfig> = {};
  itemEntries.forEach(([id, item], index) => {
    updatedItems[id] = { ...item, order: index + 1 };
  });

  updateMainPageOrderProp(updatedItems);
  return updatedItems;
};

export const updateVisibility = (
  items: Record<string, ItemDisplayConfig>,
  itemId: string,
  isHidden: boolean
): Record<string, ItemDisplayConfig> => {
  const itemEntries = Object.entries(items).sort(
    ([, a], [, b]) => a.order - b.order
  );
  const visibleEntriesLength = itemEntries.filter(
    (entry) => !entry[1].isHidden
  ).length;

  const currentIndex = itemEntries.findIndex(([id]) => id === itemId);
  const [itemToUpdate] = itemEntries.splice(currentIndex, 1);

  const updatedEntry: [string, ItemDisplayConfig] = [
    itemToUpdate[0],
    { ...itemToUpdate[1], isHidden },
  ];

  if (isHidden) {
    itemEntries.push([
      updatedEntry[0],
      { order: updatedEntry[1].order, isHidden: updatedEntry[1].isHidden },
    ]);
  } else {
    itemEntries.splice(visibleEntriesLength, 0, [
      updatedEntry[0],
      { ...updatedEntry[1], order: visibleEntriesLength + 1 },
    ]);
  }

  const updatedItems: Record<string, ItemDisplayConfig> = {};
  itemEntries.forEach(([id, item], index) => {
    updatedItems[id] = { ...item, order: index + 1 };
  });

  updateMainPageOrderProp(updatedItems);
  return updatedItems;
};

export const deleteItem = (
  items: Record<string, ItemDisplayConfig>,
  itemId: string
): Record<string, ItemDisplayConfig> => {
  const updatedItems: Record<string, ItemDisplayConfig> = {};

  Object.entries(items)
    .filter(([id]) => id !== itemId)
    .sort(([, a], [, b]) => a.order - b.order)
    .forEach(([id, item], index) => {
      updatedItems[id] = { ...item, order: index + 1 };
    });

  updateMainPageOrderProp(updatedItems);
  return updatedItems;
};
